import { useEffect, useMemo, useState } from "react";
import { Monitor, Tablet, Smartphone, Mail, AlertTriangle, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";

const STORAGE_KEY = "cf_preview_samples_v1";

// Known placeholders → default sample values
const DEFAULT_SAMPLES = {
  name: "Jane Doe",
  first_name: "Jane",
  last_name: "Doe",
  company: "Acme Inc",
  email: "jane@acme.com",
  title: "Head of Growth",
  city: "San Francisco",
  industry: "SaaS",
  website: "acme.com",
  phone: "(415) 555-0142",
};

// Title-Case a snake_case / kebab-case var as a last-resort sample value
const humanize = (v) =>
  v.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const extractVars = (text) => {
  const out = new Set();
  const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  let m;
  while ((m = re.exec(text)) !== null) out.add(m[1]);
  return Array.from(out);
};

const render = (tpl, vars) =>
  (tpl || "").replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (_, key) =>
    vars[key] !== undefined && vars[key] !== "" ? vars[key] : humanize(key)
  );

const DEVICES = [
  { key: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
  { key: "tablet", label: "Tablet", icon: Tablet, width: "768px" },
  { key: "mobile", label: "Mobile", icon: Smartphone, width: "375px" },
];

const CLIENTS = [
  { key: "generic", label: "Generic", emoji: "✉" },
  { key: "gmail", label: "Gmail", emoji: "G" },
  { key: "outlook", label: "Outlook", emoji: "O" },
  { key: "apple", label: "Apple Mail", emoji: "A" },
];

// CSS injected into the iframe to simulate each client's rendering quirks
const CLIENT_CSS = {
  generic: "",
  apple: "/* Apple Mail: most permissive — no overrides */",
  gmail: `
    /* Gmail: strips <style> in some contexts, no position fixed/absolute */
    body { background: #f6f8fc !important; }
    [style*="position: absolute"], [style*="position:absolute"],
    [style*="position: fixed"], [style*="position:fixed"] {
      position: static !important;
    }
    /* Gmail blocks some custom fonts — fall back to Arial */
    body, body * { font-family: Arial, Helvetica, sans-serif !important; }
  `,
  outlook: `
    /* Outlook desktop (Word renderer): strips border-radius, box-shadow, transforms, bg images */
    *, *::before, *::after {
      border-radius: 0 !important;
      box-shadow: none !important;
      transform: none !important;
      background-image: none !important;
      filter: none !important;
    }
    /* Outlook ignores flex/grid */
    [style*="display: flex"], [style*="display:flex"],
    [style*="display: grid"], [style*="display:grid"] {
      display: block !important;
    }
    /* Outlook default font */
    body, body * { font-family: "Calibri", "Segoe UI", sans-serif !important; }
    svg { display: none !important; }
  `,
};

// Detect known rendering issues for the given client
const detectIssues = (html, client) => {
  if (!html) return [];
  const issues = [];
  const sizeKb = new Blob([html]).size / 1024;
  if (client === "gmail" && sizeKb > 102) {
    issues.push(`Email is ${sizeKb.toFixed(1)} KB — Gmail clips messages over 102 KB and shows "View entire message" link`);
  }
  if (client === "outlook") {
    if (/border-radius\s*:/i.test(html)) issues.push("border-radius is stripped — buttons/cards will have sharp corners");
    if (/background-image\s*:/i.test(html)) issues.push("background-image is stripped — use solid fill or VML fallback");
    if (/box-shadow\s*:/i.test(html)) issues.push("box-shadow is not rendered");
    if (/display\s*:\s*(flex|grid)/i.test(html)) issues.push("flex/grid layouts collapse to block — use <table> for layout");
    if (/<svg/i.test(html)) issues.push("SVG icons don't render — use PNG fallbacks");
    if (/transform\s*:/i.test(html)) issues.push("CSS transforms are ignored");
    if (/@media/i.test(html)) issues.push("@media queries do not work — use width/style on <table>");
  }
  if (client === "gmail") {
    if (/position\s*:\s*(absolute|fixed)/i.test(html)) issues.push("position: absolute/fixed is forced to static");
    if (/<style[^>]*>/i.test(html) && !/style=/i.test(html)) issues.push("Use inline styles — Gmail can strip <style> blocks in clipped messages");
  }
  return issues;
};

const buildSrcDoc = ({ body, bodyType, sampleVars, client }) => {
  const rendered = render(body, sampleVars);
  const clientCss = CLIENT_CSS[client] || "";
  const head = `<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${clientCss}</style>`;

  if (bodyType === "html") {
    if (/<head[\s>]/i.test(rendered)) {
      // inject our client CSS after existing head
      return rendered.replace(/<head([^>]*)>/i, `<head$1>${head}`);
    }
    if (/<html[\s>]/i.test(rendered)) {
      return rendered.replace(/<html([^>]*)>/i, `<html$1><head>${head}</head>`);
    }
    return `<!DOCTYPE html><html><head>${head}</head><body style="margin:0">${rendered}</body></html>`;
  }
  // text mode — escape + newlines→<br>
  const escaped = rendered
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
  return `<!DOCTYPE html><html><head>${head}<style>
    body{margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#000;background:#ffffff;}
    a{color:#0a58ca;}
  </style></head><body>${escaped}</body></html>`;
};

export default function EmailPreview({ subject = "", body = "", bodyType = "text", testid = "email-preview" }) {
  const [device, setDevice] = useState("desktop");
  const [client, setClient] = useState("generic");
  const [showSamples, setShowSamples] = useState(false);
  const [overrides, setOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  });

  // Detect all placeholders used across subject + body
  const detectedVars = useMemo(
    () => Array.from(new Set([...extractVars(subject), ...extractVars(body)])),
    [subject, body]
  );

  // Build the effective sample registry
  const sampleVars = useMemo(() => {
    const out = { ...DEFAULT_SAMPLES };
    for (const v of detectedVars) {
      if (out[v] === undefined) out[v] = humanize(v);
    }
    for (const [k, v] of Object.entries(overrides)) {
      if (v !== undefined && v !== "") out[k] = v;
    }
    return out;
  }, [detectedVars, overrides]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides)); } catch { /* storage may be full or disabled */ }
  }, [overrides]);

  const html = useMemo(
    () => buildSrcDoc({ body, bodyType, sampleVars, client }),
    [body, bodyType, sampleVars, client]
  );
  const renderedSubject = useMemo(() => render(subject, sampleVars), [subject, sampleVars]);
  const issues = useMemo(() => detectIssues(bodyType === "html" ? body : "", client), [body, bodyType, client]);

  const cur = DEVICES.find((d) => d.key === device) || DEVICES[0];
  const senderEmail = sampleVars.email || DEFAULT_SAMPLES.email;
  const senderName = sampleVars.name || DEFAULT_SAMPLES.name;

  const updateOverride = (key, val) => setOverrides((o) => ({ ...o, [key]: val }));
  const resetOverrides = () => setOverrides({});

  return (
    <div className="cf-card p-0 overflow-hidden" data-testid={testid}>
      {/* Top bar */}
      <div className="p-4 border-b border-zinc-800 flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// preview / variables substituted</div>
          <div className="text-white font-medium truncate mt-1">{renderedSubject || <span className="text-zinc-600">No subject</span>}</div>
          <div className="font-mono text-[10px] text-zinc-600 mt-0.5">From: you@coldforge.dev → {senderName} &lt;{senderEmail}&gt;</div>
        </div>
        <div className="flex flex-col gap-2">
          {/* Device toggle */}
          <div className="flex gap-1">
            {DEVICES.map((d) => {
              const Icon = d.icon;
              const active = d.key === device;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDevice(d.key)}
                  data-testid={`preview-device-${d.key}`}
                  className={`px-3 py-2 border font-mono text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2 ${
                    active ? "border-white bg-zinc-900 text-white" : "border-zinc-800 text-zinc-500 hover:text-white"
                  }`}
                >
                  <Icon size={12} /> {d.label}
                </button>
              );
            })}
          </div>
          {/* Client toggle */}
          <div className="flex gap-1">
            {CLIENTS.map((c) => {
              const active = c.key === client;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setClient(c.key)}
                  data-testid={`preview-client-${c.key}`}
                  className={`px-3 py-2 border font-mono text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2 ${
                    active ? "border-[#E23A28] bg-zinc-900 text-[#E23A28]" : "border-zinc-800 text-zinc-500 hover:text-white"
                  }`}
                >
                  <span className="font-display font-bold text-[10px]">{c.emoji}</span> {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sample data registry */}
      <div className="border-b border-zinc-800">
        <button
          type="button"
          onClick={() => setShowSamples((s) => !s)}
          data-testid="toggle-sample-data"
          className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-zinc-900/40 transition-colors"
        >
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {showSamples ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            // sample data{detectedVars.length > 0 && ` / ${detectedVars.length} variable${detectedVars.length === 1 ? "" : "s"} detected`}
          </div>
          {Object.keys(overrides).length > 0 && (
            <span className="font-mono text-[10px] text-[#E23A28]">{Object.keys(overrides).length} customized</span>
          )}
        </button>
        {showSamples && (
          <div className="px-4 pb-4" data-testid="sample-data-panel">
            {detectedVars.length === 0 ? (
              <div className="font-mono text-[10px] text-zinc-600 py-2">
                // no {"{{placeholders}}"} used in this email yet
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {detectedVars.map((v) => (
                    <div key={v}>
                      <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
                        {"{{"}{v}{"}}"}
                      </label>
                      <input
                        type="text"
                        value={overrides[v] ?? sampleVars[v] ?? ""}
                        onChange={(e) => updateOverride(v, e.target.value)}
                        data-testid={`sample-input-${v}`}
                        className="cf-input"
                        style={{ padding: "6px 10px", fontSize: "12px" }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={resetOverrides}
                    data-testid="reset-samples"
                    className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1"
                  >
                    <RotateCcw size={10} /> Reset to defaults
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Client-specific issues */}
      {issues.length > 0 && (
        <div className="px-4 py-3 border-b border-zinc-800 bg-amber-950/20" data-testid="client-issues">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2">
            <AlertTriangle size={11} /> // {client} rendering notes
          </div>
          <ul className="space-y-1">
            {issues.map((iss, i) => (
              <li key={i} data-testid={`client-issue-${i}`} className="text-zinc-300 text-xs flex gap-2">
                <span className="text-amber-400 flex-shrink-0">→</span>
                <span>{iss}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Iframe */}
      <div className="bg-zinc-950 p-6 flex justify-center">
        <div
          className="bg-white shadow-2xl transition-all duration-300 ease-out"
          style={{
            width: cur.width,
            maxWidth: "100%",
            height: cur.key === "mobile" ? "640px" : cur.key === "tablet" ? "720px" : "600px",
          }}
        >
          <iframe
            title="Email preview"
            srcDoc={html}
            sandbox=""
            className="w-full h-full border-0"
            data-testid={`${testid}-iframe`}
          />
        </div>
      </div>
    </div>
  );
}
